import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUrlTab } from '../../components/ui';
import ParticipantFile from './ParticipantFile';
import AgedCareDashboard from './Dashboard';
import AgedCareParticipants from './Participants';
import AgedCareServiceList from './ServiceList';
import AgedCareSahBudgets from './SahBudgets';
import AgedCareCarePlans from './CarePlans';
import AgedCareSirs from './Sirs';
import AgedCareComplaints from './Complaints';
import AgedCareWorkerCompliance from './WorkerCompliance';
import AgedCareFunding from './Funding';
import AgedCareLeave from './Leave';
import AgedCareProviders from './Providers';
import AgedCareSupplierInvoices from './SupplierInvoices';
import AgedCareStatements from './Statements';
import AgedCareClaims from './Claims';
import AgedCareRates from './Rates';
import AgedCareDex from './Dex';
import AgedCareClaimItems from './ClaimItems';
import AgedCareGovReports from './GovReports';
import AgedCareCareManagement from './CareManagement';
import AgedCareQuotes from './Quotes';
import AgedCareChsp from './Chsp';
import AgedCareReferrals from './Referrals';

// One hub, everything inside — mirrors Brevity's HCP "Package Management" menu but
// targets Support at Home (SAH) alongside legacy Home Care Packages (HCP).
// These are the ?tab= values the sidebar (AgedCareNav) and other screens link to; do not rename them.
const TAB_KEYS = [
  'dashboard', 'participants', 'services', 'budgets', 'careplans', 'sirs', 'complaints', 'compliance',
  'funding', 'leave', 'providers', 'invoices', 'statements', 'claims', 'rates', 'dex', 'items', 'reports', 'caremgmt', 'quotes',
  'chsp', 'referrals', 'participant',
];

export default function AgedCare() {
  // The open screen lives in the address (?tab=), so a refresh or a shared link lands on the same
  // screen. Changing it pushes a history entry, so Back returns to the previous screen instead of
  // leaving the module. The overview is the bare /aged-care, as the sidebar links to it.
  const [tab, setTab] = useUrlTab('tab', 'dashboard', TAB_KEYS);
  const [params, setParams] = useSearchParams();
  // Moving to another screen leaves the participant behind (no stale ?id= on the next screen).
  const goTo = useCallback((k) => {
    if (!TAB_KEYS.includes(k) || k === tab) return;
    if (params.has('id') || params.has('view')) setParams(k === 'dashboard' ? {} : { tab: k });
    else setTab(k);
  }, [tab, setTab, params, setParams]);
  // A participant's file: /aged-care?tab=participant&id=…&view=funding
  const openParticipant = useCallback((id, view) => {
    if (!id) return;
    setParams(view && view !== 'overview' ? { tab: 'participant', id, view } : { tab: 'participant', id });
  }, [setParams]);
  const setView = useCallback((v) => {
    setParams((p) => { const n = new URLSearchParams(p); if (v && v !== 'overview') n.set('view', v); else n.delete('view'); return n; });
  }, [setParams]);

  function renderTab() {
    switch (tab) {
      case 'services':   return <AgedCareServiceList />;
      case 'budgets':    return <AgedCareSahBudgets />;
      case 'careplans':  return <AgedCareCarePlans />;
      case 'sirs':       return <AgedCareSirs />;
      case 'complaints': return <AgedCareComplaints />;
      case 'compliance': return <AgedCareWorkerCompliance />;
      case 'funding':    return <AgedCareFunding />;
      case 'leave':      return <AgedCareLeave />;
      case 'providers':  return <AgedCareProviders />;
      case 'invoices':   return <AgedCareSupplierInvoices />;
      case 'statements': return <AgedCareStatements />;
      case 'claims':     return <AgedCareClaims />;
      case 'rates':      return <AgedCareRates />;
      case 'dex':        return <AgedCareDex />;
      case 'chsp':       return <AgedCareChsp onGoTo={goTo} />;
      case 'referrals':  return <AgedCareReferrals onGoTo={goTo} />;
      case 'items':      return <AgedCareClaimItems />;
      case 'reports':    return <AgedCareGovReports />;
      case 'caremgmt':   return <AgedCareCareManagement />;
      case 'quotes':     return <AgedCareQuotes onGoTo={goTo} />;
      case 'participants': return <AgedCareParticipants onGoTo={goTo} onOpen={openParticipant} />;
      case 'participant':
        return params.get('id')
          ? <ParticipantFile key={params.get('id')} clientId={params.get('id')} section={params.get('view') || 'overview'} onSection={setView} onGoTo={goTo} />
          : <AgedCareParticipants onGoTo={goTo} onOpen={openParticipant} />;
      case 'dashboard':
      default:           return <AgedCareDashboard onGoTo={goTo} />;
    }
  }

  return (
    // No outer padding or max width: <main> already pads the page, and Plan Management and Support
    // Coordination use this same container, so the left edge stays put between the care modules.
    // The screens are picked in the main navigation (Aged Care section), like PM and SC; the app
    // header names the module and each screen's own heading is the page's h1.
    <div className="py-2 w-full">
      {renderTab()}
    </div>
  );
}
